import React, { useEffect, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ApiEndpoints from './commonComponents/ApiEndpoints'

const TOTAL_STEPS = 5

const ORG_TYPES = [
  { id: 'school', label: 'School', icon: 'bi-building', description: 'K-12 schools & academies' },
  { id: 'college', label: 'College', icon: 'bi-mortarboard', description: 'Colleges & training institutes' },
  { id: 'coaching', label: 'Coaching', icon: 'bi-book', description: 'Tuition & coaching centers' },
]

const STUDENT_STRENGTH = [
  { id: 'lt_100', label: 'Less than 100' },
  { id: '100_500', label: '100–500' },
  { id: '500_1000', label: '500–1000' },
  { id: '1000_5000', label: '1000–5000' },
  { id: 'gt_5000', label: 'More than 5000' },
]

/** Upper bound sent as `student_allowed_count` for each strength tier. */
const STUDENT_STRENGTH_MAX_COUNT = {
  lt_100: 100,
  100_500: 500,
  500_1000: 1000,
  1000_5000: 5000,
  gt_5000: 10000,
}

/** Local-part@domain with TLD of at least 2 letters (rejects e.g. user@fd.c). */
const EMAIL_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]{0,62}[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
/** Indian mobile: 10 digits starting with 6–9. */
const PHONE_REGEX = /^[6-9]\d{9}$/
const PINCODE_REGEX = /^[1-9][0-9]{5}$/

/** Values accepted by POST /register (`institute_type`). */
const INSTITUTE_TYPE_API_MAP = {
  school: 'school',
  college: 'college',
  coaching: 'coaching',
}

export const REGISTRATION_RESULT_KEY = 'pendingRegistrationLogin'

const STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
]

const CITY_BY_STATE = {
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati'],
  'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat'],
  Assam: ['Guwahati', 'Silchar', 'Dibrugarh'],
  Bihar: ['Patna', 'Gaya', 'Muzaffarpur'],
  Chhattisgarh: ['Raipur', 'Bhilai', 'Durg', 'Korba'],
  Goa: ['Panaji', 'Margao', 'Vasco da Gama'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot'],
  Haryana: ['Gurugram', 'Faridabad', 'Panipat', 'Karnal', 'Rohtak'],
  'Himachal Pradesh': ['Shimla', 'Solan', 'Dharamshala', 'Mandi'],
  Jharkhand: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Hazaribagh'],
  Karnataka: ['Bengaluru', 'Mysuru', 'Hubballi', 'Mangalore', 'Belagavi'],
  Kerala: ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain'],
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad'],
  Manipur: ['Imphal', 'Thoubal'],
  Meghalaya: ['Shillong', 'Tura', 'Nongstoin'],
  Mizoram: ['Aizawl', 'Lunglei'],
  Nagaland: ['Kohima', 'Dimapur'],
  Odisha: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur'],
  Punjab: ['Amritsar', 'Ludhiana', 'Jalandhar', 'Patiala'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer'],
  Sikkim: ['Gangtok', 'Namchi'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli'],
  Telangana: ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam'],
  Tripura: ['Agartala', 'Udaipur', 'Dharmanagar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Meerut'],
  Uttarakhand: ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Kashipur'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
  'Andaman and Nicobar Islands': ['Port Blair', 'Havelock', 'Car Nicobar'],
  Chandigarh: ['Chandigarh'],
  'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
  Delhi: ['New Delhi', 'Delhi'],
  'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla'],
  Ladakh: ['Leh', 'Kargil'],
  Lakshadweep: ['Kavaratti', 'Minicoy', 'Agatti'],
  Puducherry: ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
}

const getOrgNameLabel = (organizationType) => {
  if (organizationType === 'school') return 'School name'
  if (organizationType === 'college') return 'College name'
  if (organizationType === 'coaching') return 'Coaching center name'
  return 'Organization name'
}

const getOrgNamePlaceholder = (organizationType) => {
  const label = getOrgNameLabel(organizationType)
  return `Enter your ${label.toLowerCase()}`
}

const getPasswordStrength = (password) => {
  if (!password) {
    return { score: 0, label: '', color: '#E5E7EB' }
  }

  let score = 0
  if (password.length >= 6) score += 1
  if (password.length >= 10) score += 1
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1
  if (/\d/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1

  if (score <= 2) return { score: 1, label: 'Weak', color: '#EF4444' }
  if (score <= 3) return { score: 2, label: 'Fair', color: '#F59E0B' }
  if (score <= 4) return { score: 3, label: 'Good', color: '#3B82F6' }
  return { score: 4, label: 'Strong', color: '#10B981' }
}

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  organizationType: '',
  organizationName: '',
  state: '',
  city: '',
  schoolPincode: '',
  studentStrength: '',
  password: '',
  confirmPassword: '',
}

/**
 * Multi-step registration wizard as a dedicated route page.
 * Submits to POST /register, then returns to Login with credentials prefilled.
 */
export default function RegistrationWizard() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [animKey, setAnimKey] = useState(0)
  const [slideDir, setSlideDir] = useState('forward')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const firstFieldRef = useRef(null)

  useEffect(() => {
    const lockScroll = () => {
      if (window.innerWidth < 992) return false
      const html = document.documentElement
      const body = document.body
      html.style.overflow = 'hidden'
      body.style.overflow = 'hidden'
      return true
    }

    const unlockScroll = (prevHtmlOverflow, prevBodyOverflow) => {
      const html = document.documentElement
      const body = document.body
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
    }

    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow

    lockScroll()

    const onResize = () => {
      if (window.innerWidth < 992) {
        unlockScroll(prevHtmlOverflow, prevBodyOverflow)
      } else {
        lockScroll()
      }
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      unlockScroll(prevHtmlOverflow, prevBodyOverflow)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      firstFieldRef.current?.focus?.()
    }, 280)
    return () => clearTimeout(timer)
  }, [step])

  const getEmailError = (value) => {
    const email = String(value ?? '').trim()
    if (!email) return 'Email address is required.'
    if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address.'
    return null
  }

  const normalizePhoneDigits = (value) => {
    let digits = String(value ?? '').replace(/\D/g, '')
    if (digits.length === 12 && digits.startsWith('91')) {
      digits = digits.slice(2)
    } else if (digits.length === 11 && digits.startsWith('0')) {
      digits = digits.slice(1)
    }
    return digits
  }

  const getPhoneError = (value) => {
    const raw = String(value ?? '').trim()
    if (!raw) return null
    const digits = normalizePhoneDigits(raw)
    if (!PHONE_REGEX.test(digits)) {
      return 'Please enter a valid 10-digit mobile number.'
    }
    return null
  }

  const updateField = (name, value) => {
    const nextValue =
      name === 'phone' ? String(value ?? '').replace(/[^\d+\s()-]/g, '').slice(0, 16) : value
    setForm((prev) => ({ ...prev, [name]: nextValue }))

    const shouldLiveValidate =
      (name === 'email' && (touched.email || errors.email)) ||
      (name === 'phone' && (touched.phone || errors.phone))

    if (shouldLiveValidate) {
      const fieldError =
        name === 'email' ? getEmailError(nextValue) : getPhoneError(nextValue)
      setErrors((prev) => {
        const next = { ...prev }
        if (fieldError) next[name] = fieldError
        else delete next[name]
        return next
      })
      return
    }

    if (touched[name] || errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const handleEmailBlur = () => {
    setTouched((t) => ({ ...t, email: true }))
    const emailError = getEmailError(form.email)
    setErrors((prev) => {
      const next = { ...prev }
      if (emailError) next.email = emailError
      else delete next.email
      return next
    })
  }

  const handlePhoneBlur = () => {
    setTouched((t) => ({ ...t, phone: true }))
    const phoneError = getPhoneError(form.phone)
    setErrors((prev) => {
      const next = { ...prev }
      if (phoneError) next.phone = phoneError
      else delete next.phone
      return next
    })
  }

  const validateStep = (currentStep) => {
    const nextErrors = {}

    if (currentStep === 1) {
      const emailError = getEmailError(form.email)
      if (emailError) nextErrors.email = emailError
      const phoneError = getPhoneError(form.phone)
      if (phoneError) nextErrors.phone = phoneError
    }

    if (currentStep === 2) {
      if (!form.organizationType) {
        nextErrors.organizationType = 'Please select an organization type.'
      }
    }

    if (currentStep === 3) {
      if (!form.organizationName.trim()) {
        nextErrors.organizationName = 'Organization name is required.'
      }
      if (!form.state) {
        nextErrors.state = 'Please select a state.'
      }
      if (!form.city) {
        nextErrors.city = 'Please select a city.'
      }
      const pin = form.schoolPincode.trim()
      if (!pin) {
        nextErrors.schoolPincode = 'Pincode is required.'
      } else if (!PINCODE_REGEX.test(pin)) {
        nextErrors.schoolPincode = 'Please enter a valid 6-digit pincode.'
      }
    }

    if (currentStep === 4) {
      if (!form.studentStrength) {
        nextErrors.studentStrength =
          'Please select your approximate student strength.'
      }
    }

    if (currentStep === 5) {
      if (!form.password) {
        nextErrors.password = 'Password is required.'
      } else if (form.password.length < 6) {
        nextErrors.password = 'Password must be at least 6 characters.'
      }
      if (!form.confirmPassword) {
        nextErrors.confirmPassword = 'Please confirm your password.'
      } else if (form.password !== form.confirmPassword) {
        nextErrors.confirmPassword = 'Passwords do not match.'
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const goNext = () => {
    if (!validateStep(step)) return
    if (step < TOTAL_STEPS) {
      setSlideDir('forward')
      setAnimKey((k) => k + 1)
      setStep((s) => s + 1)
      setErrors({})
    }
  }

  const goBack = () => {
    if (step === 1) {
      navigate('/LoginSignup')
      return
    }
    setSlideDir('back')
    setAnimKey((k) => k + 1)
    setStep((s) => s - 1)
    setErrors({})
  }

  const handleFinish = async () => {
    if (!validateStep(5) || isSubmitting) return

    const payload = {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: normalizePhoneDigits(form.phone),
      organizationType: form.organizationType,
      organizationName: form.organizationName.trim(),
      state: form.state,
      city: form.city,
      schoolPincode: form.schoolPincode.trim(),
      studentStrength: form.studentStrength,
      password: form.password,
      completedAt: new Date().toISOString(),
    }

    const registerBody = {
      name: payload.fullName || payload.organizationName,
      email: payload.email,
      password: payload.password,
      phone_number: payload.phone,
      school_name: payload.organizationName,
      school_address: [payload.city, payload.state].filter(Boolean).join(', '),
      school_pincode: payload.schoolPincode,
      institute_type:
        INSTITUTE_TYPE_API_MAP[payload.organizationType] || 'school',
      student_allowed_count:
        STUDENT_STRENGTH_MAX_COUNT[payload.studentStrength] ?? 100,
    }

    setIsSubmitting(true)
    try {
      const response = await ApiEndpoints.register(registerBody)
      const message =
        response?.data?.message || 'Registration completed successfully.'

      try {
        sessionStorage.setItem(REGISTRATION_RESULT_KEY, JSON.stringify(payload))
        sessionStorage.setItem(
          'registrationWizardData',
          JSON.stringify({
            fullName: payload.fullName,
            email: payload.email,
            phone: payload.phone,
            organizationType: payload.organizationType,
            organizationName: payload.organizationName,
            state: payload.state,
            city: payload.city,
            schoolPincode: payload.schoolPincode,
            studentStrength: payload.studentStrength,
            completedAt: payload.completedAt,
          }),
        )
      } catch (_) {
        // sessionStorage may be unavailable
      }

      toast.success(message || 'Details saved. Please sign in to continue.')
      navigate('/LoginSignup', {
        replace: true,
        state: {
          fromRegistration: true,
          email: payload.email,
          password: payload.password,
        },
      })
    } catch (error) {
      const data = error.response?.data
      let errorMessage = data?.message || 'Registration failed. Please try again.'

      if (data?.errors && typeof data.errors === 'object') {
        const fieldMessages = Object.values(data.errors).flat().filter(Boolean)
        if (fieldMessages.length > 0) {
          errorMessage = fieldMessages.join(' ')
        }
      }

      const isDuplicateEmail =
        String(errorMessage).includes('Duplicate entry') ||
        /email.*(already|exist)|already.*email/i.test(String(errorMessage))

      if (isDuplicateEmail) {
        const emailMsg = 'Email already exists. Please use a different email.'
        setSlideDir('back')
        setAnimKey((k) => k + 1)
        setStep(1)
        setTouched((t) => ({ ...t, email: true }))
        setErrors({ email: emailMsg })
        toast.error(emailMsg)
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault()
      if (step < TOTAL_STEPS) goNext()
      else handleFinish()
    }
  }

  const strength = getPasswordStrength(form.password)
  const progressPct = (step / TOTAL_STEPS) * 100

  const stepMeta = {
    1: {
      title: "Let's Get Started",
      subtitle: 'Please provide your basic information to continue.',
    },
    2: {
      title: 'Choose Your Organization Type',
      subtitle: 'Select the option that best describes your organization.',
    },
    3: {
      title: 'Enter Organization Details',
      subtitle: 'Provide basic details about your organization.',
    },
    4: {
      title: 'Tell Us About Your Organization',
      subtitle: 'Approximately how many students do you have?',
    },
    5: {
      title: 'Create Your Password',
      subtitle: 'Create a secure password to continue.',
    },
  }

  return (
    <>
      <style>
        {`
          .reg-wizard-page {
            flex: 1 1 auto;
            width: 100%;
            min-height: 0;
            height: 100%;
            max-height: 100dvh;
            background: #F3F4F6;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px 12px;
            box-sizing: border-box;
            overflow: hidden;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .reg-wizard-layout {
            width: 100%;
            max-width: 680px;
            max-height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          .reg-wizard-shell {
            width: 100%;
            max-width: 680px;
            max-height: 100%;
            flex: 1 1 auto;
            min-height: 0;
            background: #ffffff;
            border-radius: 16px;
            border: none;
            // box-shadow: 0 16px 40px rgba(34, 56, 67, 0.12);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .reg-wizard-account-link {
            flex-shrink: 0;
            margin-top: 10px;
            text-align: center;
            font-size: 13px;
            color: #6B7280;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .reg-wizard-signin-link {
            background: none;
            border: none;
            padding: 0;
            margin: 0;
            font-size: 13px;
            font-weight: 600;
            color: #223843;
            cursor: pointer;
            text-decoration: underline;
            text-underline-offset: 2px;
            font-family: 'Inter', system-ui, sans-serif;
            transition: color 0.2s ease;
          }

          .reg-wizard-signin-link:hover {
            color: #1a2d35;
          }

          .reg-wizard-signin-link:focus-visible {
            outline: 2px solid #223843;
            outline-offset: 2px;
            border-radius: 4px;
          }

          .reg-wizard-header {
            border-bottom: 1px solid #E5E7EB;
            padding: 12px 18px 10px;
            background: linear-gradient(180deg, #ffffff 0%, #F9FAFB 100%);
            flex-shrink: 0;
          }

          .reg-wizard-top-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 14px 18px 6px;
          }

          .reg-wizard-top-logo-wrap {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            border: 2px solid #223843;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            padding: 10px;
            box-sizing: border-box;
            box-shadow: 0 4px 14px rgba(34, 56, 67, 0.08);
          }

          .reg-wizard-top-logo-wrap img {
            width: 100%;
            height: 100%;
            max-height: none;
            object-fit: contain;
            opacity: 0.95;
          }

          .reg-wizard-body {
            padding: 14px 18px;
            min-height: 0;
            flex: 1 1 auto;
            overflow: hidden;
          }

          .reg-wizard-footer {
            border-top: 1px solid #E5E7EB;
            padding: 10px 18px 12px;
            background: #F9FAFB;
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 8px;
            flex-shrink: 0;
          }

          .reg-wizard-progress-track {
            height: 5px;
            background: #E5E7EB;
            border-radius: 999px;
            overflow: hidden;
            margin-top: 8px;
          }

          .reg-wizard-progress-fill {
            height: 100%;
            border-radius: 999px;
            background: linear-gradient(90deg, #223843 0%, #3d5a68 100%);
            transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .reg-wizard-step-label {
            font-size: 11px;
            font-weight: 600;
            color: #6B7280;
            letter-spacing: 0.02em;
          }

          .reg-wizard-title {
            font-size: 1.15rem;
            font-weight: 700;
            color: #223843;
            margin: 0 0 4px;
            line-height: 1.3;
          }

          .reg-wizard-subtitle {
            font-size: 0.8rem;
            color: #6B7280;
            margin: 0 0 12px;
            line-height: 1.4;
          }

          .reg-wizard-panel {
            animation-duration: 0.35s;
            animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            animation-fill-mode: both;
          }

          .reg-wizard-panel.forward {
            animation-name: regWizardSlideInRight;
          }

          .reg-wizard-panel.back {
            animation-name: regWizardSlideInLeft;
          }

          @keyframes regWizardSlideInRight {
            from { opacity: 0; transform: translateX(28px); }
            to { opacity: 1; transform: translateX(0); }
          }

          @keyframes regWizardSlideInLeft {
            from { opacity: 0; transform: translateX(-28px); }
            to { opacity: 1; transform: translateX(0); }
          }

          .reg-wizard-label {
            font-size: 11px;
            font-weight: 600;
            color: #223843;
            margin-bottom: 4px;
          }

          .reg-wizard-input {
            border: 1.5px solid #E5E7EB;
            border-radius: 8px;
            padding: 7px 10px;
            font-size: 12.5px;
            color: #223843;
            background: #ffffff;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            min-height: 34px;
          }

          .reg-wizard-input:focus {
            border-color: #223843;
            box-shadow: 0 0 0 3px rgba(34, 56, 67, 0.12);
            outline: none;
            color: #223843;
          }

          .reg-wizard-input.is-invalid {
            border-color: #EF4444;
          }

          .reg-wizard-error {
            font-size: 11px;
            color: #EF4444;
            margin-top: 4px;
          }

          .reg-wizard-option-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 8px;
          }

          @media (min-width: 576px) {
            .reg-wizard-option-grid.org-type {
              grid-template-columns: repeat(3, 1fr);
            }
          }

          .reg-wizard-option-card {
            border: 1.5px solid #E5E7EB;
            border-radius: 12px;
            background: #ffffff;
            padding: 12px 10px;
            text-align: center;
            cursor: pointer;
            transition: all 0.22s ease;
            box-shadow: 0 2px 8px rgba(34, 56, 67, 0.04);
            width: 100%;
            color: inherit;
          }

          .reg-wizard-option-card:hover {
            border-color: #223843;
            transform: translateY(-1px);
            box-shadow: 0 6px 14px rgba(34, 56, 67, 0.1);
          }

          .reg-wizard-option-card:focus-visible {
            outline: 2px solid #223843;
            outline-offset: 2px;
          }

          .reg-wizard-option-card.selected {
            border-color: #223843;
            background: linear-gradient(180deg, #F3F4F6 0%, #ffffff 100%);
            box-shadow: 0 0 0 3px rgba(34, 56, 67, 0.12), 0 6px 14px rgba(34, 56, 67, 0.08);
          }

          .reg-wizard-option-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            margin: 0 auto 8px;
            border-radius: 10px;
            background: #F3F4F6;
            color: #223843;
            font-size: 1.2rem;
            line-height: 1;
            transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;
          }

          .reg-wizard-option-card:hover .reg-wizard-option-icon {
            transform: scale(1.05);
          }

          .reg-wizard-option-card.selected .reg-wizard-option-icon {
            background: #223843;
            color: #ffffff;
          }

          .reg-wizard-option-label {
            font-size: 13px;
            font-weight: 700;
            color: #223843;
            margin-bottom: 2px;
          }

          .reg-wizard-option-desc {
            font-size: 10.5px;
            color: #6B7280;
            margin: 0;
            line-height: 1.3;
          }

          .reg-wizard-strength-list {
            display: grid;
            gap: 8px;
          }

          .reg-wizard-strength-item {
            border: 1.5px solid #E5E7EB;
            border-radius: 10px;
            background: #ffffff;
            padding: 10px 14px;
            text-align: left;
            font-size: 13px;
            font-weight: 600;
            color: #223843;
            cursor: pointer;
            transition: all 0.22s ease;
            width: 100%;
          }

          .reg-wizard-strength-item:hover {
            border-color: #223843;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(34, 56, 67, 0.1);
          }

          .reg-wizard-strength-item:focus-visible {
            outline: 2px solid #223843;
            outline-offset: 2px;
          }

          .reg-wizard-strength-item.selected {
            border-color: #223843;
            background: #223843;
            color: #ffffff;
            box-shadow: 0 6px 14px rgba(34, 56, 67, 0.22);
          }

          .reg-wizard-pwd-wrap {
            position: relative;
          }

          .reg-wizard-pwd-toggle {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #223843;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .reg-wizard-pwd-toggle:hover {
            color: #1a2d35;
          }

          .reg-wizard-strength {
            margin-top: 8px;
          }

          .reg-wizard-strength-bars {
            display: flex;
            gap: 5px;
            margin-bottom: 4px;
          }

          .reg-wizard-strength-bar {
            flex: 1;
            height: 4px;
            border-radius: 999px;
            background: #E5E7EB;
            transition: background 0.25s ease;
          }

          .reg-wizard-strength-meta {
            font-size: 11px;
            font-weight: 600;
          }

          .reg-wizard-btn-primary {
            background: linear-gradient(135deg, #223843 0%, #1a2d35 100%);
            border: none;
            border-radius: 8px;
            padding: 8px 18px;
            font-weight: 600;
            font-size: 13px;
            color: #ffffff;
            min-width: 96px;
            transition: all 0.2s ease;
          }

          .reg-wizard-btn-primary:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 14px rgba(34, 56, 67, 0.3);
            background: linear-gradient(135deg, #1a2d35 0%, #15252c 100%);
            color: #ffffff;
          }

          .reg-wizard-btn-primary:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .reg-wizard-btn-secondary {
            background: #ffffff;
            border: 1.5px solid #E5E7EB;
            border-radius: 8px;
            padding: 7px 16px;
            font-weight: 600;
            font-size: 13px;
            color: #223843;
            min-width: 90px;
            transition: all 0.2s ease;
          }

          .reg-wizard-btn-secondary:hover {
            border-color: #223843;
            background: #F3F4F6;
            color: #223843;
          }

          .reg-wizard-body .mb-3 {
            margin-bottom: 0.65rem !important;
          }

          .reg-wizard-body .mb-1 {
            margin-bottom: 0.25rem !important;
          }

          @media (max-width: 575.98px) {
            .reg-wizard-page {
              padding: 8px;
              align-items: center;
            }

            .reg-wizard-shell {
              border-radius: 14px;
            }

            .reg-wizard-body {
              padding: 12px 14px;
            }

            .reg-wizard-header,
            .reg-wizard-footer {
              padding-left: 14px;
              padding-right: 14px;
            }

            .reg-wizard-title {
              font-size: 1.05rem;
            }

            .reg-wizard-footer {
              flex-direction: row;
              align-items: stretch;
            }

            .reg-wizard-btn-primary,
            .reg-wizard-btn-secondary {
              flex: 1;
              min-width: 0;
            }
          }

          @media (max-height: 700px) {
            .reg-wizard-page {
              padding: 6px 10px;
            }

            .reg-wizard-header {
              padding-top: 8px;
              padding-bottom: 8px;
            }

            .reg-wizard-body {
              padding-top: 10px;
              padding-bottom: 10px;
            }

            .reg-wizard-footer {
              padding-top: 8px;
              padding-bottom: 8px;
            }

            .reg-wizard-title {
              font-size: 1rem;
            }

            .reg-wizard-subtitle {
              margin-bottom: 8px;
              font-size: 0.75rem;
            }

            .reg-wizard-option-card {
              padding: 8px 8px;
            }

            .reg-wizard-option-icon {
              width: 34px;
              height: 34px;
              margin-bottom: 4px;
              font-size: 1.05rem;
            }

            .reg-wizard-strength-item {
              padding: 8px 12px;
              font-size: 12px;
            }
          }

          .login-page-footer {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 20;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 4px 8px;
            padding: 8px 12px;
            padding-bottom: max(8px, env(safe-area-inset-bottom, 0px));
            background: rgba(243, 244, 246, 0.95);
            backdrop-filter: blur(10px);
            border-top: 1px solid rgba(34, 56, 67, 0.08);
            box-sizing: border-box;
          }

          .login-page-footer .powered-by-text {
            font-size: 12px;
            color: #6B7280;
            font-family: 'Inter', system-ui, sans-serif;
            font-weight: 500;
            white-space: nowrap;
            pointer-events: auto;
            position: relative;
            z-index: 2;
          }

          .login-page-footer .vibhum-logo-link {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            margin-left: -1.8rem !important;
            position: relative;
            z-index: 1;
          }

          .login-page-footer .vibhum-logo-img {
            width: 108px;
            height: auto;
            max-height: 36px;
            object-fit: contain;
            opacity: 0.95;
            transition: transform 0.2s ease, opacity 0.2s ease;
          }

          .login-page-footer .vibhum-logo-link:hover .vibhum-logo-img {
            transform: scale(1.03);
            opacity: 1;
          }

          @media (max-width: 991.98px) {
            .login-page-footer .vibhum-logo-img {
              width: 88px;
              max-height: 30px;
            }

            .login-page-footer .vibhum-logo-link {
              margin-left: 0 !important;
            }
          }
        `}
      </style>

      <div className="reg-wizard-page">
        <div className="reg-wizard-layout">
        <div
          className="reg-wizard-shell"
          role="region"
          aria-labelledby="registration-wizard-title"
        >
          <div className="reg-wizard-top-logo">
            <div className="reg-wizard-top-logo-wrap">
              <img
                src="/poweredby_logo.webp"
                alt="VibhumEduSys logo"
              />
            </div>
          </div>
          <div className="reg-wizard-header">
            <div className="d-flex justify-content-between align-items-center">
              <span className="reg-wizard-step-label">
                Step {step} of {TOTAL_STEPS}
              </span>
              <span className="reg-wizard-step-label" aria-hidden="true">
                {Math.round(progressPct)}%
              </span>
            </div>
            <div
              className="reg-wizard-progress-track"
              role="progressbar"
              aria-valuenow={step}
              aria-valuemin={1}
              aria-valuemax={TOTAL_STEPS}
              aria-label={`Registration progress, step ${step} of ${TOTAL_STEPS}`}
            >
              <div
                className="reg-wizard-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <div className="reg-wizard-body" onKeyDown={handleKeyDown}>
            <div key={animKey} className={`reg-wizard-panel ${slideDir}`}>
              <h1 id="registration-wizard-title" className="reg-wizard-title">
                {stepMeta[step].title}
              </h1>
              <p className="reg-wizard-subtitle">{stepMeta[step].subtitle}</p>

              {step === 1 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-fullName">
                      Full Name
                    </Form.Label>
                    <Form.Control
                      id="reg-fullName"
                      ref={firstFieldRef}
                      type="text"
                      className="reg-wizard-input"
                      placeholder="Enter your full name"
                      value={form.fullName}
                      onChange={(e) => updateField('fullName', e.target.value)}
                      autoComplete="name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-email">
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      id="reg-email"
                      type="email"
                      className={`reg-wizard-input ${errors.email ? 'is-invalid' : ''}`}
                      placeholder="Enter your email address"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      onBlur={handleEmailBlur}
                      autoComplete="email"
                      required
                      aria-required="true"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'reg-email-error' : undefined}
                    />
                    {errors.email && (
                      <div id="reg-email-error" className="reg-wizard-error" role="alert">
                        {errors.email}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-1">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-phone">
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      id="reg-phone"
                      type="tel"
                      className={`reg-wizard-input ${errors.phone ? 'is-invalid' : ''}`}
                      placeholder="Enter 10-digit mobile number"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      onBlur={handlePhoneBlur}
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={16}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? 'reg-phone-error' : undefined}
                    />
                    {errors.phone && (
                      <div id="reg-phone-error" className="reg-wizard-error" role="alert">
                        {errors.phone}
                      </div>
                    )}
                  </Form.Group>
                </>
              )}

              {step === 2 && (
                <>
                  <div
                    className="reg-wizard-option-grid org-type"
                    role="radiogroup"
                    aria-label="Organization type"
                  >
                    {ORG_TYPES.map((org, index) => (
                      <button
                        key={org.id}
                        type="button"
                        role="radio"
                        aria-checked={form.organizationType === org.id}
                        className={`reg-wizard-option-card ${
                          form.organizationType === org.id ? 'selected' : ''
                        }`}
                        onClick={() => updateField('organizationType', org.id)}
                        ref={index === 0 ? firstFieldRef : undefined}
                      >
                        <span className="reg-wizard-option-icon" aria-hidden="true">
                          <i className={`bi ${org.icon}`} />
                        </span>
                        <div className="reg-wizard-option-label">{org.label}</div>
                        <p className="reg-wizard-option-desc">{org.description}</p>
                      </button>
                    ))}
                  </div>
                  {errors.organizationType && (
                    <div className="reg-wizard-error mt-3" role="alert">
                      {errors.organizationType}
                    </div>
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label
                      className="reg-wizard-label"
                      htmlFor="reg-organizationName"
                    >
                      {getOrgNameLabel(form.organizationType)}
                    </Form.Label>
                    <Form.Control
                      id="reg-organizationName"
                      ref={firstFieldRef}
                      type="text"
                      className={`reg-wizard-input ${
                        errors.organizationName ? 'is-invalid' : ''
                      }`}
                      placeholder={getOrgNamePlaceholder(form.organizationType)}
                      value={form.organizationName}
                      onChange={(e) =>
                        updateField('organizationName', e.target.value)
                      }
                      autoComplete="organization"
                      aria-invalid={!!errors.organizationName}
                      aria-describedby={
                        errors.organizationName ? 'reg-orgname-error' : undefined
                      }
                    />
                    {errors.organizationName && (
                      <div
                        id="reg-orgname-error"
                        className="reg-wizard-error"
                        role="alert"
                      >
                        {errors.organizationName}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-state">
                      State <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      id="reg-state"
                      className={`reg-wizard-input ${errors.state ? 'is-invalid' : ''}`}
                      value={form.state}
                      onChange={(e) => {
                        updateField('state', e.target.value)
                        updateField('city', '')
                      }}
                      aria-invalid={!!errors.state}
                      aria-describedby={errors.state ? 'reg-state-error' : undefined}
                    >
                      <option value="">Select state</option>
                      {STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Control>
                    {errors.state && (
                      <div
                        id="reg-state-error"
                        className="reg-wizard-error"
                        role="alert"
                      >
                        {errors.state}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-city">
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      id="reg-city"
                      className={`reg-wizard-input ${errors.city ? 'is-invalid' : ''}`}
                      value={form.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      aria-invalid={!!errors.city}
                      aria-describedby={errors.city ? 'reg-city-error' : undefined}
                      disabled={!form.state}
                    >
                      <option value="">
                        {form.state ? 'Select city' : 'Select state first'}
                      </option>
                      {(CITY_BY_STATE[form.state] || []).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Form.Control>
                    {errors.city && (
                      <div
                        id="reg-city-error"
                        className="reg-wizard-error"
                        role="alert"
                      >
                        {errors.city}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-1">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-pincode">
                      Pincode <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      id="reg-pincode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className={`reg-wizard-input ${
                        errors.schoolPincode ? 'is-invalid' : ''
                      }`}
                      placeholder="Enter 6-digit pincode"
                      value={form.schoolPincode}
                      onChange={(e) =>
                        updateField(
                          'schoolPincode',
                          e.target.value.replace(/\D/g, '').slice(0, 6),
                        )
                      }
                      aria-invalid={!!errors.schoolPincode}
                      aria-describedby={
                        errors.schoolPincode ? 'reg-pincode-error' : undefined
                      }
                    />
                    {errors.schoolPincode && (
                      <div
                        id="reg-pincode-error"
                        className="reg-wizard-error"
                        role="alert"
                      >
                        {errors.schoolPincode}
                      </div>
                    )}
                  </Form.Group>
                </>
              )}

              {step === 4 && (
                <>
                  <div
                    className="reg-wizard-strength-list"
                    role="radiogroup"
                    aria-label="Student strength"
                  >
                    {STUDENT_STRENGTH.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        role="radio"
                        aria-checked={form.studentStrength === item.id}
                        className={`reg-wizard-strength-item ${
                          form.studentStrength === item.id ? 'selected' : ''
                        }`}
                        onClick={() => updateField('studentStrength', item.id)}
                        ref={index === 0 ? firstFieldRef : undefined}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  {errors.studentStrength && (
                    <div className="reg-wizard-error mt-3" role="alert">
                      {errors.studentStrength}
                    </div>
                  )}
                </>
              )}

              {step === 5 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-password">
                      Enter Password
                    </Form.Label>
                    <div className="reg-wizard-pwd-wrap">
                      <Form.Control
                        id="reg-password"
                        ref={firstFieldRef}
                        type={showPassword ? 'text' : 'password'}
                        className={`reg-wizard-input ${errors.password ? 'is-invalid' : ''}`}
                        placeholder="Create a password"
                        value={form.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        style={{ paddingRight: '42px' }}
                        autoComplete="new-password"
                        aria-invalid={!!errors.password}
                        aria-describedby={
                          errors.password ? 'reg-password-error' : 'reg-password-strength'
                        }
                      />
                      <button
                        type="button"
                        className="reg-wizard-pwd-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                    {form.password && (
                      <div className="reg-wizard-strength" id="reg-password-strength">
                        <div className="reg-wizard-strength-bars" aria-hidden="true">
                          {[1, 2, 3, 4].map((n) => (
                            <div
                              key={n}
                              className="reg-wizard-strength-bar"
                              style={{
                                background:
                                  strength.score >= n ? strength.color : '#E5E7EB',
                              }}
                            />
                          ))}
                        </div>
                        <div
                          className="reg-wizard-strength-meta"
                          style={{ color: strength.color }}
                        >
                          Password strength: {strength.label}
                        </div>
                      </div>
                    )}
                    {errors.password && (
                      <div id="reg-password-error" className="reg-wizard-error" role="alert">
                        {errors.password}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-1">
                    <Form.Label className="reg-wizard-label" htmlFor="reg-confirmPassword">
                      Confirm Password
                    </Form.Label>
                    <div className="reg-wizard-pwd-wrap">
                      <Form.Control
                        id="reg-confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={`reg-wizard-input ${
                          errors.confirmPassword ? 'is-invalid' : ''
                        }`}
                        placeholder="Confirm your password"
                        value={form.confirmPassword}
                        onChange={(e) => updateField('confirmPassword', e.target.value)}
                        style={{ paddingRight: '42px' }}
                        autoComplete="new-password"
                        aria-invalid={!!errors.confirmPassword}
                        aria-describedby={
                          errors.confirmPassword ? 'reg-confirm-error' : undefined
                        }
                      />
                      <button
                        type="button"
                        className="reg-wizard-pwd-toggle"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        aria-label={
                          showConfirmPassword
                            ? 'Hide confirm password'
                            : 'Show confirm password'
                        }
                      >
                        {showConfirmPassword ? (
                          <FaEyeSlash size={16} />
                        ) : (
                          <FaEye size={16} />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <div id="reg-confirm-error" className="reg-wizard-error" role="alert">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </Form.Group>
                </>
              )}
            </div>
          </div>

          <div className="reg-wizard-footer">
            <Button
              type="button"
              className="reg-wizard-btn-secondary"
              onClick={goBack}
            >
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />
              Back
            </Button>

            {step < TOTAL_STEPS ? (
              <Button
                type="button"
                className="reg-wizard-btn-primary"
                onClick={goNext}
              >
                Next
                <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                type="button"
                className="reg-wizard-btn-primary"
                onClick={handleFinish}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
              >
                <i className="bi bi-check2-circle me-1" aria-hidden="true" />
                {isSubmitting ? 'Please wait...' : 'Finish'}
              </Button>
            )}
          </div>
        </div>

        <p className="reg-wizard-account-link">
          Already have account?{' '}
          <button
            type="button"
            className="reg-wizard-signin-link"
            onClick={() => navigate('/LoginSignup')}
          >
            Sign in
          </button>
        </p>
        </div>
      </div>

      <footer className="login-page-footer" aria-label="Registration page footer">
        <span className="powered-by-text">Powered by <strong>VibhumEduSys</strong></span>
        <a
          className="vibhum-logo-link"
          href="https://vibhumedusys.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="VibhumEduSys (opens in a new tab)"
        >
          <img
            className="vibhum-logo-img"
            src="/poweredby_logo.webp"
            alt="VibhumEduSys logo"
          />
        </a>
      </footer>
    </>
  )
}
